/**
 * CrackedTileManager — Placement tiles far from player bases start cracked.
 *
 * Cracked tiles cannot be used for robot placement until repaired.
 * Repair options:
 *   1. Repair robots heal cracked tiles within their support range during waves
 *   2. Player buys tile repairs during workshop/intermission (cost ∝ distance from base)
 *
 * Uses tile_broken_placement sprite for cracked visuals.
 * Emits TILE_REPAIRED event when a tile is repaired.
 */

import Phaser from 'phaser';
import { TILE_SIZE, MAP_COLS, MAP_ROWS, TileType, BASE_VISION_RADIUS, FOG_PENUMBRA_RADIUS } from '@/config/GameConfig';
import { MAP_GRID, BASE_POSITIONS } from '@/map/MapData';
import { EventBus, GameEvents } from '@/events/EventBus';
import { GOD } from '@/config/GodMode';
import { makeRenderTexture } from '@/utils/PhaserHelpers';

// ── Config ──

/** Tiles within the player base's full field of vision are NOT cracked.
 *  Matches base vision radius + penumbra so all visible tiles are usable. */
const SAFE_RADIUS_TILES = BASE_VISION_RADIUS + FOG_PENUMBRA_RADIUS;
/** Base cost ($) for repairing a cracked tile at distance = SAFE_RADIUS + 1 */
const BASE_REPAIR_COST = 5;
/** Cost multiplier per extra tile of distance beyond safe radius */
const COST_PER_TILE = 3;
/** HP a cracked tile has (repair robots heal this) */
const TILE_MAX_HP = 40;
/** Distance snap: tiles within this many tiles of each other get the same cost */
const DIST_SNAP_TILES = 1; // ~30px = 1 tile

/** Tile key for fast set operations */
function tileKey(col: number, row: number): number {
  return row * MAP_COLS + col;
}

export interface CrackedTile {
  col: number;
  row: number;
  hp: number;       // 0 = fully cracked, TILE_MAX_HP = repaired
  maxHp: number;
  distance: number; // distance in tiles from nearest base
}

/** Per-tile overlay: broken sprite + optional cost label */
interface TileOverlay {
  sprite: Phaser.GameObjects.Image;
  border: Phaser.GameObjects.Sprite;  // batchable sprite instead of Rectangle
  costLabel: Phaser.GameObjects.Text;
}

export class CrackedTileManager {
  private scene: Phaser.Scene;

  /** Cracked tile state indexed by tileKey */
  private tiles: Map<number, CrackedTile> = new Map();
  /** Visual overlays per tile (destroyed on repair) */
  private overlays: Map<number, TileOverlay> = new Map();
  /** LED tile images placed over repaired tiles */
  private repairedImages: Phaser.GameObjects.Image[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── Initialization ──

  /** Scan the map and mark distant PLACEMENT/GRILLE tiles as cracked */
  init(): void {
    for (let row = 0; row < MAP_ROWS; row++) {
      for (let col = 0; col < MAP_COLS; col++) {
        const tt = MAP_GRID[row][col];
        if (tt !== TileType.PLACEMENT && tt !== TileType.GRILLE && tt !== TileType.BROKEN_PLACEMENT) continue;

        const dist = this.distanceToNearestBase(col, row);
        if (dist <= SAFE_RADIUS_TILES) continue;

        // Snap distance to bands so nearby tiles share the same repair cost
        const snapped = Math.round(dist / DIST_SNAP_TILES) * DIST_SNAP_TILES;
        const key = tileKey(col, row);
        this.tiles.set(key, {
          col, row,
          hp: 0,
          maxHp: TILE_MAX_HP,
          distance: snapped,
        });
        this.createOverlay(col, row);
      }
    }
    this.refreshOverlayStates();
  }

  /** Euclidean distance (in tiles) from tile center to nearest player base */
  private distanceToNearestBase(col: number, row: number): number {
    let minDist = Infinity;
    const cx = col + 0.5;
    const cy = row + 0.5;
    for (const bp of BASE_POSITIONS) {
      const bCol = bp.x / TILE_SIZE;
      const bRow = bp.y / TILE_SIZE;
      const dx = cx - bCol;
      const dy = cy - bRow;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < minDist) minDist = d;
    }
    return minDist;
  }

  // ── Queries ──

  /** Is this tile cracked (blocks placement)? */
  isCracked(col: number, row: number): boolean {
    const t = this.tiles.get(tileKey(col, row));
    return !!t && t.hp < t.maxHp;
  }

  /** Get cracked tile info (or null if not cracked/fully repaired) */
  getTile(col: number, row: number): CrackedTile | null {
    const t = this.tiles.get(tileKey(col, row));
    if (!t || t.hp >= t.maxHp) return null;
    return t;
  }

  /** Cost to instantly repair a cracked tile (proportional to distance) */
  getRepairCost(col: number, row: number): number {
    const t = this.tiles.get(tileKey(col, row));
    if (!t) return 0;
    const extraDist = Math.max(0, t.distance - SAFE_RADIUS_TILES);
    const cost = BASE_REPAIR_COST + Math.round(extraDist * COST_PER_TILE);
    return Math.round(cost * GOD.tileRepairCostMult);
  }

  /**
   * Can this tile be repaired right now?
   * Returns false if there are cheaper cracked tiles still available —
   * player must unlock cheaper tiers first.
   */
  canRepairTile(col: number, row: number): boolean {
    const t = this.tiles.get(tileKey(col, row));
    if (!t || t.hp >= t.maxHp) return false;
    const myCost = this.getRepairCost(col, row);
    // Check if any cracked tile has a strictly lower cost
    for (const other of this.tiles.values()) {
      if (other === t) continue;
      if (other.hp >= other.maxHp) continue; // already repaired
      const otherCost = this.getRepairCost(other.col, other.row);
      if (otherCost < myCost) return false;
    }
    return true;
  }

  /** Get all cracked tiles within pixel range of a world position */
  getCrackedTilesNear(wx: number, wy: number, range: number): CrackedTile[] {
    const results: CrackedTile[] = [];
    const rangeSq = range * range;
    for (const t of this.tiles.values()) {
      if (t.hp >= t.maxHp) continue;
      const tcx = (t.col + 0.5) * TILE_SIZE;
      const tcy = (t.row + 0.5) * TILE_SIZE;
      const dx = tcx - wx;
      const dy = tcy - wy;
      if (dx * dx + dy * dy <= rangeSq) {
        results.push(t);
      }
    }
    return results;
  }

  /** Count of tiles still cracked */
  get crackedCount(): number {
    let n = 0;
    for (const t of this.tiles.values()) {
      if (t.hp < t.maxHp) n++;
    }
    return n;
  }

  // ── Repair actions ──

  /** Instantly repair a tile (player purchase). Returns true if repaired. */
  repairTile(col: number, row: number): boolean {
    const key = tileKey(col, row);
    const t = this.tiles.get(key);
    if (!t || t.hp >= t.maxHp) return false;
    t.hp = t.maxHp;
    this.destroyOverlay(key);
    this.placeRepairedTile(col, row);
    EventBus.emit(GameEvents.TILE_REPAIRED, { col, row });
    this.refreshOverlayStates(); // newly repairable tiles become brighter
    return true;
  }

  /** Heal a cracked tile by `amount` HP (repair robot aura). Returns HP actually healed. */
  healTile(col: number, row: number, amount: number): number {
    const key = tileKey(col, row);
    const t = this.tiles.get(key);
    if (!t || t.hp >= t.maxHp) return 0;
    const before = t.hp;
    t.hp = Math.min(t.maxHp, t.hp + amount);
    // Update overlay opacity (fades as tile heals)
    this.updateOverlayAlpha(key, t);
    // Fully repaired?
    if (t.hp >= t.maxHp) {
      this.destroyOverlay(key);
      this.placeRepairedTile(col, row);
      EventBus.emit(GameEvents.TILE_REPAIRED, { col, row });
      this.refreshOverlayStates(); // newly repairable tiles become brighter
    }
    return t.hp - before;
  }

  // ── Visuals ──

  /** Place broken_placement sprite over the tile */
  private createOverlay(col: number, row: number): void {
    const key = tileKey(col, row);
    const cx = col * TILE_SIZE + TILE_SIZE / 2;
    const cy = row * TILE_SIZE + TILE_SIZE / 2;

    // Broken tile sprite — sits on top of the normal tile (depth 0.3)
    const sprite = this.scene.add.image(cx, cy, 'tile_broken_placement');
    sprite.setDisplaySize(TILE_SIZE, TILE_SIZE);
    sprite.setDepth(0.3);

    // Subtle danger border — use a cached sprite for batching instead of Rectangle
    const borderKey = '_cracked_border';
    if (!this.scene.textures.exists(borderKey)) {
      const bs = TILE_SIZE;
      const rt = makeRenderTexture(this.scene, bs, bs);
      const gfx = this.scene.make.graphics({});
      gfx.lineStyle(1, 0xff6633, 0.35);
      gfx.strokeRect(0.5, 0.5, bs - 2, bs - 2);
      rt.draw(gfx, 0, 0);
      rt.saveTexture(borderKey);
      rt.destroy();
      gfx.destroy();
    }
    const border = this.scene.add.sprite(cx, cy, borderKey);
    border.setDepth(1.1);

    // Cost label — hidden by default to save GPU (shown on hover/click via UIScene)
    const t = this.tiles.get(key);
    const cost = t ? this.getRepairCost(col, row) : 0;
    const costLabel = this.scene.add.text(
      cx + TILE_SIZE / 2 - 2,
      cy + TILE_SIZE / 2 - 2,
      `$${cost}`,
      { fontSize: '7px', fontFamily: 'monospace', color: '#ff8844', stroke: '#000000', strokeThickness: 2 },
    ).setOrigin(1, 1).setDepth(1.2).setVisible(false);

    this.overlays.set(key, { sprite, border, costLabel });
  }

  /** Refresh all overlays: repairable tiles look normal, blocked ones get a
   *  distance-based gradient — closer = brighter, farther = darker. */
  refreshOverlayStates(): void {
    // Use full distance range across ALL cracked tiles for maximum gradient spread
    let maxDist = 0;
    for (const t of this.tiles.values()) {
      if (t.hp >= t.maxHp) continue;
      if (t.distance > maxDist) maxDist = t.distance;
    }
    // Range starts from safe radius (where cracked tiles begin)
    const distRange = Math.max(1, maxDist - SAFE_RADIUS_TILES);

    for (const [key, t] of this.tiles.entries()) {
      if (t.hp >= t.maxHp) continue;
      const ov = this.overlays.get(key);
      if (!ov) continue;
      const repairable = this.canRepairTile(t.col, t.row);
      if (repairable) {
        ov.sprite.setAlpha(1).clearTint();
        ov.border.setAlpha(0.35).setTint(0xff6633);
      } else {
        // Same look as repairable but slightly darker to hint "not yet available"
        ov.sprite.setAlpha(1).setTint(0x999999);
        ov.border.setAlpha(0.25).setTint(0xff6633);
      }
    }
  }

  /** Fade overlay as tile heals */
  private updateOverlayAlpha(key: number, t: CrackedTile): void {
    const ov = this.overlays.get(key);
    if (!ov) return;
    const ratio = 1 - t.hp / t.maxHp; // 1 = fully cracked, 0 = repaired
    ov.sprite.setAlpha(ratio);
    ov.border.setAlpha(ratio * 0.35);
    // costLabel stays hidden — shown only on hover/click
  }

  /** Remove overlay when tile is fully repaired */
  private destroyOverlay(key: number): void {
    const ov = this.overlays.get(key);
    if (ov) {
      ov.sprite.destroy();
      ov.border.destroy();
      ov.costLabel.destroy();
      this.overlays.delete(key);
    }
  }

  /** Place LED tile image over a repaired tile (covers the base tile_placement) */
  private placeRepairedTile(col: number, row: number): void {
    const cx = col * TILE_SIZE + TILE_SIZE / 2;
    const cy = row * TILE_SIZE + TILE_SIZE / 2;
    const img = this.scene.add.image(cx, cy, 'tile_placement_led');
    // Apply current tileDarken tint immediately so it matches existing tiles
    const b = Math.round((1 - GOD.tileDarken) * 255);
    img.setDisplaySize(TILE_SIZE, TILE_SIZE).setDepth(0.1).setTint((b << 16) | (b << 8) | b);
    this.repairedImages.push(img);
  }

  /** Get all repaired tile images (for GameScene to apply tileDarken tint) */
  getRepairedImages(): Phaser.GameObjects.Image[] {
    return this.repairedImages;
  }

  // ── Cleanup ──

  shutdown(): void {
    for (const ov of this.overlays.values()) {
      ov.sprite.destroy();
      ov.border.destroy();
      ov.costLabel.destroy();
    }
    this.overlays.clear();
    for (const img of this.repairedImages) img.destroy();
    this.repairedImages = [];
    this.tiles.clear();
  }
}
